import { motion } from 'framer-motion';
import { AlertTriangle, CalendarClock, TrendingDown, Wallet } from 'lucide-react';
import {
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import { useFormationB2BDettes, useFormationB2BKpis } from '@/hooks/queries/useFormationB2B';

const STATUT_TONE: Record<string, UniversalStatus> = {
  ouverte: 'en_attente',
  partiellement_payee: 'en_cours',
  payee: 'paye',
  en_retard: 'en_retard',
  a_payer: 'a_payer',
  planifie: 'planifie',
  paye: 'paye',
  annule: 'annule',
  annulee: 'annule',
};

export function DettesTab(): JSX.Element {
  const { data: k, isLoading: kpisLoading } = useFormationB2BKpis();
  const { data: dettes = [], isLoading } = useFormationB2BDettes();

  const enRetard = dettes.filter((d) => d.statut === 'en_retard').length;
  const topFournisseur =
    dettes.length === 0
      ? null
      : Object.entries(
          dettes.reduce<Record<string, number>>((acc, d) => {
            const k = d.fournisseur_nom ?? 'Inconnu';
            acc[k] = (acc[k] ?? 0) + Number(d.montant_du);
            return acc;
          }, {}),
        ).sort((a, b) => b[1] - a[1])[0];

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="finances"
          icon={<Wallet className="h-5 w-5" />}
          title="Dettes Fournisseurs B2B"
          subtitle="Formateurs, hôtels, traiteurs, marketing — toutes les charges liées aux sessions."
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="finances"
            label="Total dettes"
            value={<MoneyAmount amount={k?.dettes_total ?? 0} variant="inline" />}
            icon={<TrendingDown className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="finances"
            label="À payer cette semaine"
            value={<MoneyAmount amount={k?.dettes_semaine ?? 0} variant="inline" />}
            icon={<CalendarClock className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="finances"
            label="En retard"
            value={enRetard}
            icon={<AlertTriangle className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="Top fournisseur"
            hint={topFournisseur ? topFournisseur[0] : undefined}
            value={
              topFournisseur ? <MoneyAmount amount={topFournisseur[1]} variant="inline" /> : '—'
            }
            loading={isLoading}
          />
        </div>

        <SectionTitle>
          {dettes.length} dette{dettes.length > 1 ? 's' : ''}
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="table" count={5} />
        ) : dettes.length === 0 ? (
          <EmptyStateModern
            variant="finances"
            icon={<Wallet className="h-7 w-7" />}
            title="Aucune dette fournisseur"
            description="Les paiements formateur, lieu, traiteur d'un dossier B2B apparaissent ici automatiquement."
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">Référence</th>
                    <th className="py-2">Fournisseur</th>
                    <th className="py-2">Type</th>
                    <th className="py-2">Échéance</th>
                    <th className="py-2 text-right">Montant</th>
                    <th className="py-2 text-right">Dû</th>
                    <th className="py-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {dettes.map((d, i) => (
                    <motion.tr
                      key={d.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i, 8) * 0.02 }}
                      className="border-t border-slate-100"
                    >
                      <td className="py-2 font-mono">{d.reference}</td>
                      <td className="py-2 font-semibold text-slate-800">
                        {d.fournisseur_nom ?? '—'}
                      </td>
                      <td className="py-2 text-slate-600">{d.fournisseur_type ?? '—'}</td>
                      <td className="py-2">
                        <DateBadge date={d.date_echeance} />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={d.montant} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={d.montant_du} variant="inline" />
                      </td>
                      <td className="py-2">
                        <StatusBadge
                          status={STATUT_TONE[d.statut] ?? 'en_attente'}
                          label={d.statut.replace(/_/g, ' ')}
                        />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}
      </div>
    </PageContainer>
  );
}
